import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Send, Users, ShieldCheck } from "lucide-react";
import api, { formatApiError, handleActError } from "@/lib/api";
import { toast } from "sonner";
import { locationModeFor, isMobilityType, CLASS_OPTIONS, paxCapFor } from "@/lib/travelTypes";
import { LocationPicker } from "@/components/LocationPicker";
import { SavedPlacePicker, RoutePicker, RecentPicker, recordRecentPlace } from "@/components/SavedPlaces";
import { PlaceAutocomplete } from "@/components/PlaceAutocomplete";
import { loadMaps } from "@/lib/geo";

// Inline "Ask for a rate" form used by the unified search — handles both a travel type (with
// its requirement fields + agent targeting) and a generic service category. Requires a verified
// mobile number to send; a 403 routes the shopper to /verify-mobile.
export const RateRequest = ({ travelType = null, categoryId = null, categoryLabel = "", loc = null, onDone, prefill = null, compact = false }) => {
  const nav = useNavigate();
  const [fields, setFields] = useState({});
  const [coords, setCoords] = useState({});
  const [tripPrev, setTripPrev] = useState(null);
  const [fareRate, setFareRate] = useState(null);
  useEffect(() => {
    if (!travelType || !isMobilityType(travelType.key)) return;
    api.get(`/marketplace/mobility-rate?travel_type=${travelType.key}`).then((r) => setFareRate(r.data)).catch(() => {});
  }, [travelType]);
  useEffect(() => {
    const a = coords.pickup, b = coords.drop;
    if (!a?.lat || !b?.lat) { setTripPrev(null); return; }
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    let alive = true;
    loadMaps(key).then(() => {
      const g = window.google;
      new g.maps.DirectionsService().route({ origin: { lat: a.lat, lng: a.lng }, destination: { lat: b.lat, lng: b.lng },
        travelMode: g.maps.TravelMode.DRIVING, drivingOptions: { departureTime: new Date(), trafficModel: g.maps.TrafficModel.BEST_GUESS } },
        (res, st) => {
          if (!alive) return;
          if (st === "OK" && res) { const leg = res.routes?.[0]?.legs?.[0]; setTripPrev({ km: ((leg?.distance?.value || 0) / 1000).toFixed(1), mins: Math.round((leg?.duration_in_traffic?.value || leg?.duration?.value || 0) / 60) }); }
          else setTripPrev(null);
        });
    }).catch(() => {});
    return () => { alive = false; };
  }, [coords.pickup, coords.drop]);
  const [pax, setPax] = useState(prefill?.pax ? { adults: prefill.pax.adults || 0, children: prefill.pax.children || 0, infants: prefill.pax.infants || 0 } : { adults: 1, children: 0, infants: 0 });
  const [groupOn, setGroupOn] = useState(false);
  const [groupSize, setGroupSize] = useState(10);
  const [applicants, setApplicants] = useState(1);
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState("all");
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [reach, setReach] = useState(null);
  const [reachLocated, setReachLocated] = useState(false);
  const [reachInArea, setReachInArea] = useState(0);
  const [reachElsewhere, setReachElsewhere] = useState(0);

  useEffect(() => { setFields(prefill?.fields || {}); if (prefill?.query) setQuery(prefill.query); if (prefill?.pax) setPax({ adults: prefill.pax.adults || 0, children: prefill.pax.children || 0, infants: prefill.pax.infants || 0 }); }, [travelType?.key, categoryId]); // eslint-disable-line
  useEffect(() => { if (travelType) api.get("/services/travel/agents").then((r) => setAgents(r.data.agents || [])).catch(() => {}); }, [travelType]);
  useEffect(() => {
    const qs = new URLSearchParams();
    if (travelType) qs.set("travel_type", travelType.key); else if (categoryId) qs.set("category_id", categoryId); else { setReach(null); return; }
    if (loc?.state) qs.set("state", loc.state);
    if (loc?.district) qs.set("district", loc.district);
    if (loc?.pincode) qs.set("pincode", loc.pincode);
    api.get(`/services/rfq/reach?${qs.toString()}`).then((r) => { setReach(r.data.reach); setReachLocated(!!r.data.located); setReachInArea(r.data.in_area || 0); setReachElsewhere(r.data.elsewhere || 0); }).catch(() => setReach(null));
  }, [travelType?.key, categoryId, loc]);

  const handleErr = (e) => {
    if (!handleActError(e, nav)) toast.error(formatApiError(e.response?.data?.detail));
  };

  const paxTotal = (pax.adults || 0) + (pax.children || 0) + (pax.infants || 0);
  const cap = travelType ? paxCapFor(travelType.key) : 9;
  const groupMin = cap + 1;

  const submit = async () => {
    if (travelType && target === "one" && !agentId) { toast.error("Choose an agent to send to."); return; }
    if (travelType?.pax && !groupOn && paxTotal > cap) { toast.error(`Up to ${cap} passengers for a normal search. Turn on Group travel for larger groups.`); return; }
    if (travelType?.pax && groupOn && (groupSize || 0) < groupMin) { toast.error(`Group travel is for ${groupMin} or more passengers.`); return; }
    setBusy(true);
    try {
      if (travelType) {
        const body = {
          subcategory: travelType.key, subcategory_label: travelType.label, travel_type: travelType.key,
          fields, notes,
          adults: travelType.pax ? pax.adults : 0, children: travelType.pax ? pax.children : 0, infants: travelType.pax ? pax.infants : 0,
          applicants: travelType.applicants ? applicants : 0,
          group: !!(travelType.pax && groupOn), group_size: travelType.pax && groupOn ? Number(groupSize) || 0 : 0,
          customer_phone: phone || null, target: target === "one" ? agentId : "all",
          state: loc?.state || null, district: loc?.district || null, pincode: loc?.pincode || null,
        };
        const r = await api.post("/services/travel/request", body);
        if (isMobilityType(travelType.key)) {
          if (fields.pickup) recordRecentPlace(fields.pickup);
          if (fields.drop) recordRecentPlace(fields.drop);
        }
        toast.success(`Sent to ${r.data.sent_to} matching provider${r.data.sent_to === 1 ? "" : "s"} — they'll quote you shortly.`);
      } else {
        const r = await api.post("/services/rfq", {
          category_id: categoryId, query, notes, customer_phone: phone || null,
          state: loc?.state || null, district: loc?.district || null, pincode: loc?.pincode || null,
        });
        toast.success(`Sent to ${r.data.sent_to} matching provider${r.data.sent_to === 1 ? "" : "s"} — they'll quote you shortly.`);
      }
      onDone && onDone();
      nav("/app/bookings");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border border-sky-200 bg-sky-50/50 ${compact ? "p-4" : "p-5"}`} data-testid="rate-request">
      <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
        <ShieldCheck className="h-4 w-4 text-sky-600" /> Ask for a rate{travelType ? ` — ${travelType.label}` : categoryLabel ? ` — ${categoryLabel}` : ""}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">Providers send you prices; you pick the best. Your contact stays private until you accept a quote.</p>

      {travelType ? (
        <div className={`mt-3 grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
          {isMobilityType(travelType.key) && tripPrev && (
            <div className="sm:col-span-2 -mb-1 flex flex-wrap items-center gap-2" data-testid="rr-trip-preview">
              <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-sky-600 px-2.5 py-1 text-[11px] font-bold text-white">
                Trip preview: ~{tripPrev.km} km · ~{tripPrev.mins} min in traffic
              </span>
              {fareRate?.count > 0 && (
                <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white" data-testid="rr-fare-range">
                  Est. ₹{Math.round(tripPrev.km * fareRate.min_rate)}–₹{Math.round(tripPrev.km * fareRate.max_rate)} <span className="font-medium opacity-90">· based on {fareRate.count} posted fare{fareRate.count === 1 ? "" : "s"}</span>
                </span>
              )}
            </div>
          )}
          {travelType.fields.map(([k, label, type]) => { const lm = locationModeFor(travelType.key, k);
            const showSaved = isMobilityType(travelType.key) && ["pickup", "drop", "from", "to"].includes(k);
            return (
            <label key={k} className="text-xs font-semibold text-slate-500">{label}
              {lm
                ? <div className="mt-1"><LocationPicker mode={lm} value={fields[k] || ""} onChange={(v) => setFields((f) => ({ ...f, [k]: v }))} testid={`rr-loc-${k}`} placeholder="Search or type a place…" near={loc ? { state: loc.state, city: loc.city, district: loc.district } : null} /></div>
                : showSaved
                  ? <PlaceAutocomplete value={fields[k] || ""} onChange={(v, c) => { setFields((f) => ({ ...f, [k]: v })); setCoords((cc) => ({ ...cc, [k]: c?.lat ? c : null })); }} testid={`rr-place-${k}`} placeholder="Search pickup / drop…" />
                  : (k === "class" && CLASS_OPTIONS[travelType.key])
                    ? <select value={fields[k] || ""} onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))} data-testid={`rr-field-${k}`} className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                        <option value="">{travelType.key === "hotel" ? "Any category" : "Any class"}</option>
                        {CLASS_OPTIONS[travelType.key].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    : <input type={type} value={fields[k] || ""} onChange={(e) => { setFields((f) => ({ ...f, [k]: e.target.value })); if (type === "date" || type === "datetime-local") e.target.blur(); }} data-testid={`rr-field-${k}`} className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />}
              {showSaved && <>
                {k === "pickup" && <RoutePicker testid="rr-routes" onPick={(r) => setFields((f) => ({ ...f, pickup: r.from, drop: r.to }))} />}
                <SavedPlacePicker testid={`rr-saved-${k}`} onPick={(p) => setFields((f) => ({ ...f, [k]: p.address }))} />
                <RecentPicker testid={`rr-recent-${k}`} onPick={(a) => setFields((f) => ({ ...f, [k]: a }))} />
              </>}
            </label>
          ); })}
        </div>
      ) : (
        <label className="mt-3 block text-xs font-semibold text-slate-500">What do you need?
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="rr-query" placeholder="Describe it briefly (e.g. 2 BHK deep clean this weekend)" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
        </label>
      )}

      {travelType?.pax && (
        <div className="mt-3" data-testid="rr-pax">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500">Number of {travelType.paxLabel || "travellers"}</p>
            <button type="button" onClick={() => setGroupOn((v) => !v)} data-testid="rr-group-toggle" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${groupOn ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-white"}`}><Users className="h-3 w-3" /> Group travel {groupOn ? "ON" : "OFF"}</button>
          </div>
          {groupOn && (
            <label className="mt-2 block text-[11px] font-semibold text-slate-600" data-testid="rr-group-size-row">Group size ({groupMin}+)
              <input type="number" min={groupMin} value={groupSize} onChange={(e) => setGroupSize(Math.max(0, parseInt(e.target.value || "0", 10)))} data-testid="rr-group-size" className="mt-0.5 h-9 w-32 rounded-md border border-slate-200 px-2 text-sm" />
            </label>
          )}
          <div className="mt-1 grid grid-cols-3 gap-2">
            {[["adults", "Adults"], ["children", "Children"], ["infants", "Infants"]].map(([k, l]) => (
              <label key={k} className="text-[11px] text-slate-500">{l}
                <input type="number" min="0" value={pax[k]} onChange={(e) => setPax((x) => ({ ...x, [k]: Math.max(0, parseInt(e.target.value || "0", 10)) }))} data-testid={`rr-pax-${k}`} className="mt-0.5 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
              </label>
            ))}
          </div>
          {!groupOn
            ? <p className={`mt-1 text-[11px] ${paxTotal > cap ? "font-semibold text-amber-600" : "text-slate-400"}`} data-testid="rr-pax-hint">{paxTotal > cap ? `Up to ${cap} for a normal search — turn on Group travel for larger groups.` : `${paxTotal} of ${cap} max (infants included).`}</p>
            : <p className="mt-1 text-[11px] text-slate-400">Group of {groupSize}+ — providers send group quotes. Optional Adults/Children/Infants breakdown above.</p>}
        </div>
      )}
      {travelType?.applicants && (
        <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-500"><Users className="h-3.5 w-3.5" /> Applicants
          <input type="number" min="1" value={applicants} onChange={(e) => setApplicants(Math.max(1, parseInt(e.target.value || "1", 10)))} data-testid="rr-applicants" className="ml-2 h-9 w-24 rounded-md border border-slate-200 px-2 text-sm" />
        </label>
      )}

      <label className="mt-3 block text-xs font-semibold text-slate-500">Notes (optional)
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rr-notes" placeholder="Preferences, flexibility, budget…" className="mt-1 h-16 w-full rounded-md border border-slate-200 p-2 text-sm" />
      </label>
      <label className="mt-3 block text-xs font-semibold text-slate-500">Your phone (optional, shared only after you accept a quote)
        <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+ ]/g, ""))} data-testid="rr-phone" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />
      </label>

      {travelType && (
        <div className="mt-3 rounded-xl bg-white/70 p-3" data-testid="rr-target">
          <p className="text-xs font-semibold text-slate-600">Who should receive this?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={() => setTarget("all")} data-testid="rr-target-all" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${target === "all" ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-white"}`}>All matching agents</button>
            <button onClick={() => setTarget("one")} data-testid="rr-target-one" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${target === "one" ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-white"}`}>One agent I choose</button>
          </div>
          {target === "one" && (
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)} data-testid="rr-target-agent" className="mt-2 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="">Choose an agent…</option>
              {agents.map((a) => <option key={a.service_id} value={a.service_id}>{a.name}</option>)}
            </select>
          )}
        </div>
      )}

      <button onClick={submit} disabled={busy} data-testid="rr-submit" className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request for quotes
      </button>
      {reach != null && <p className="mt-2 text-center text-[11px] font-semibold text-sky-700" data-testid="rr-reach">This will reach {reach} matching provider{reach === 1 ? "" : "s"}{reachLocated ? ` — ${reachInArea} in your area${reachElsewhere > 0 ? ` · ${reachElsewhere} elsewhere` : ""}` : ""}.</p>}
    </div>
  );
};
